<script setup>
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useAlert } from 'dashboard/composables';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CardsAPI from 'custom/dashboard/api/cards';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { taskStatusLabel } from '../cardStatus';

// The card's audit trail, laid out as the currency migration's timeline: what happened, when to
// the second, who did it, and the values it changed. A comment's text opens on demand.
const props = defineProps({
  card: { type: Object, required: true },
  activities: { type: Array, required: true },
});

const { t, te } = useI18n();
const { formatMessage } = useMessageFormatter();

const entries = ref([]);
const isLoading = ref(true);
const expanded = ref([]);

const load = async () => {
  try {
    const { data } = await CardsAPI.getHistory(props.card.id);
    entries.value = data.payload;
  } catch {
    useAlert(t('CARDS.HISTORY.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);
// The page replaces the card after each change and the activities after each comment, so either
// means there is news in the history.
watch(() => [props.card, ...props.activities], load);

const DOT_CLASSES = {
  resolved: 'bg-n-teal-9',
  canceled: 'bg-n-ruby-9',
  comment_deleted: 'bg-n-ruby-9',
  task_deleted: 'bg-n-ruby-9',
};

// An event the dashboard does not know yet still reads, under its own name.
const label = entry => {
  const key = `CARDS.HISTORY.EVENTS.${entry.event.toUpperCase()}`;
  return te(key) ? t(key) : entry.event;
};

const none = value => value || t('CARDS.HISTORY.NONE');
const change = (from, to) => `${none(from)} → ${none(to)}`;
const formatDate = value =>
  value && format(new Date(value), 'dd/MM/yyyy HH:mm');
const priority = value => value && t(`CARDS.PRIORITY.${value}`);

const DETAILS = {
  stage_changed: d =>
    d.from_stage_name ? change(d.from_stage_name, d.stage_name) : d.stage_name,
  task_created: d => d.title,
  task_assigned: d => `${d.title} → ${none(d.assignee_name)}`,
  task_status_changed: d => `${d.title} → ${taskStatusLabel({}, d.status, t)}`,
  task_due_changed: d => `${d.title} → ${none(formatDate(d.due_at))}`,
  task_deleted: d => d.title,
  comment_created: d =>
    [
      d.title,
      d.attachments &&
        t('CARDS.HISTORY.FILES', { count: d.attachments }, d.attachments),
    ]
      .filter(Boolean)
      .join(' · '),
  name_changed: d => change(d.from, d.to),
  priority_changed: d => change(priority(d.from), priority(d.to)),
  due_changed: d => change(formatDate(d.from), formatDate(d.to)),
  team_changed: d => change(d.from, d.to),
  labels_changed: d =>
    [
      ...d.added.map(name => `+${name}`),
      ...d.removed.map(name => `−${name}`),
    ].join(' '),
  assignee_added: d => none(d.name),
  assignee_removed: d => none(d.name),
};

const detailOf = entry => DETAILS[entry.event]?.(entry.details);

const timestamp = value => format(new Date(value), 'dd/MM/yyyy HH:mm:ss');

// The texts an entry opens: what a comment said, or the text before and after its edit.
const textsOf = entry => {
  if (entry.event === 'comment_edited')
    return [
      { key: 'BEFORE', content: entry.details.from },
      { key: 'AFTER', content: entry.details.to },
    ];
  return entry.content ? [{ key: null, content: entry.content }] : [];
};

const isExpanded = id => expanded.value.includes(id);

const toggle = id => {
  expanded.value = isExpanded(id)
    ? expanded.value.filter(item => item !== id)
    : [...expanded.value, id];
};

const html = content => formatMessage(content || '', false, true);
</script>

<template>
  <Spinner
    v-if="isLoading"
    :size="16"
    class="text-n-slate-10"
    data-testid="card-history-loading"
  />
  <p
    v-else-if="!entries.length"
    class="mb-0 text-sm text-n-slate-11"
    data-testid="card-history-empty"
  >
    {{ $t('CARDS.HISTORY.EMPTY') }}
  </p>
  <ol v-else class="flex flex-col m-0 list-none" data-testid="card-history">
    <li
      v-for="entry in entries"
      :key="entry.id"
      class="flex gap-3"
      :data-history-event="entry.event"
    >
      <!-- The rail: a dot per entry and a line joining it to the next one. -->
      <div class="flex flex-col items-center gap-1 pt-1.5">
        <span
          class="rounded-full size-2 shrink-0"
          :class="DOT_CLASSES[entry.event] || 'bg-n-slate-8'"
        />
        <span class="flex-1 w-px bg-n-weak" />
      </div>

      <div class="flex flex-col flex-1 min-w-0 gap-1 pb-4">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ label(entry) }}
          </span>
          <span class="font-mono text-xs text-n-slate-10">
            {{ timestamp(entry.created_at) }}
          </span>
          <span class="text-xs text-n-slate-10" data-testid="history-actor">
            {{ entry.actor?.name || $t('CARDS.HISTORY.SYSTEM') }}
          </span>
        </div>

        <p
          v-if="detailOf(entry)"
          class="mb-0 text-sm break-words text-n-slate-11"
          data-testid="history-detail"
        >
          {{ detailOf(entry) }}
        </p>

        <template v-if="textsOf(entry).length">
          <button
            type="button"
            class="flex items-center gap-1 text-xs w-fit text-n-blue-text hover:underline"
            data-testid="history-toggle"
            @click="toggle(entry.id)"
          >
            <Icon
              :icon="
                isExpanded(entry.id)
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
              class="size-3.5"
            />
            {{
              $t(
                entry.event === 'comment_edited'
                  ? 'CARDS.HISTORY.SHOW_CHANGE'
                  : 'CARDS.HISTORY.SHOW_COMMENT'
              )
            }}
          </button>
          <div
            v-if="isExpanded(entry.id)"
            class="flex flex-col gap-2 mt-1"
            data-testid="history-texts"
          >
            <div v-for="text in textsOf(entry)" :key="text.key">
              <span v-if="text.key" class="text-xs font-medium text-n-slate-11">
                {{ $t(`CARDS.HISTORY.${text.key}`) }}
              </span>
              <div
                v-dompurify-html="html(text.content)"
                class="p-2 mt-1 text-sm break-words rounded-lg text-n-slate-12 bg-n-alpha-1 [&_p]:mb-2 [&_p:last-child]:mb-0"
              />
            </div>
          </div>
        </template>
      </div>
    </li>
  </ol>
</template>
